/**
 * Utility functions for video generation notifications
 * Shared between VideoGenerationCard and global notification hook
 */

export function playNotificationSound(type: 'success' | 'error') {
  try {
    // Play voice first
    speakNotification(type);
    
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    if (type === 'success') {
      // Success: Epic 5-second celebration melody - MAX VOLUME
      const melody = [
        { freq: 523, start: 0, duration: 0.3 },       // C5
        { freq: 659, start: 0.25, duration: 0.3 },    // E5
        { freq: 784, start: 0.5, duration: 0.3 },     // G5
        { freq: 1047, start: 0.75, duration: 0.4 },   // C6
        { freq: 987, start: 1.1, duration: 0.25 },    // B5
        { freq: 1047, start: 1.3, duration: 0.25 },   // C6
        { freq: 1319, start: 1.5, duration: 0.4 },    // E6
        { freq: 1568, start: 1.85, duration: 0.5 },   // G6
        { freq: 2093, start: 2.3, duration: 0.7 },    // C7 (climax)
        { freq: 1568, start: 2.95, duration: 0.3 },   // G6
        { freq: 2093, start: 3.2, duration: 1.3 },    // C7 (long ending)
      ];

      melody.forEach(note => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.type = 'sine';
        osc.frequency.setValueAtTime(note.freq, ctx.currentTime + note.start);
        gain.gain.setValueAtTime(1.0, ctx.currentTime + note.start);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + note.start + note.duration);
        
        osc.start(ctx.currentTime + note.start);
        osc.stop(ctx.currentTime + note.start + note.duration);
      });

      // Add harmony chords at key moments
      const chords = [
        { freqs: [659, 1047], start: 0.75, duration: 0.4 },   // E5 + C6
        { freqs: [784, 1319], start: 1.5, duration: 0.4 },    // G5 + E6
        { freqs: [1047, 1568, 2093], start: 2.3, duration: 0.7 }, // C6 + G6 + C7 (big chord)
      ];

      chords.forEach(chord => {
        chord.freqs.forEach(freq => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'triangle';
          osc.frequency.setValueAtTime(freq, ctx.currentTime + chord.start);
          gain.gain.setValueAtTime(0.3, ctx.currentTime + chord.start);
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + chord.start + chord.duration);
          osc.start(ctx.currentTime + chord.start);
          osc.stop(ctx.currentTime + chord.start + chord.duration);
        });
      });

      // Add sparkle/shimmer effect at the end
      for (let i = 0; i < 8; i++) {
        const sparkle = ctx.createOscillator();
        const sparkleGain = ctx.createGain();
        sparkle.connect(sparkleGain);
        sparkleGain.connect(ctx.destination);
        sparkle.type = 'sine';
        sparkle.frequency.setValueAtTime(2093 + (i * 100), ctx.currentTime + 3.5 + (i * 0.08));
        sparkleGain.gain.setValueAtTime(0.4, ctx.currentTime + 3.5 + (i * 0.08));
        sparkleGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 3.8 + (i * 0.08));
        sparkle.start(ctx.currentTime + 3.5 + (i * 0.08));
        sparkle.stop(ctx.currentTime + 3.8 + (i * 0.08));
      }

    } else {
      // Error: Epic 5-second dramatic alarm - MAX VOLUME
      const duration = 5.0;
      
      // Main alarm with pulsing intensity
      for (let pulse = 0; pulse < 5; pulse++) {
        const osc1 = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const gain = ctx.createGain();
        
        osc1.type = 'square';
        osc2.type = 'sawtooth';
        osc1.connect(gain);
        osc2.connect(gain);
        gain.connect(ctx.destination);
        
        const startTime = pulse * 1.0;
        const freq = 440 - (pulse * 60); // Descend each pulse
        
        osc1.frequency.setValueAtTime(freq, ctx.currentTime + startTime);
        osc2.frequency.setValueAtTime(freq, ctx.currentTime + startTime);
        osc1.frequency.exponentialRampToValueAtTime(freq * 0.7, ctx.currentTime + startTime + 0.5);
        osc2.frequency.exponentialRampToValueAtTime(freq * 0.7, ctx.currentTime + startTime + 0.5);
        
        gain.gain.setValueAtTime(0, ctx.currentTime + startTime);
        gain.gain.linearRampToValueAtTime(1.0, ctx.currentTime + startTime + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + startTime + 0.6);
        
        osc1.start(ctx.currentTime + startTime);
        osc2.start(ctx.currentTime + startTime);
        osc1.stop(ctx.currentTime + startTime + 0.6);
        osc2.stop(ctx.currentTime + startTime + 0.6);
      }

      // Add continuous siren wobble
      const siren = ctx.createOscillator();
      const sirenGain = ctx.createGain();
      const sirenLFO = ctx.createOscillator();
      const lfoGain = ctx.createGain();
      
      sirenLFO.frequency.setValueAtTime(3, ctx.currentTime);
      lfoGain.gain.setValueAtTime(80, ctx.currentTime);
      sirenLFO.connect(lfoGain);
      lfoGain.connect(siren.frequency);
      
      siren.type = 'triangle';
      siren.frequency.setValueAtTime(350, ctx.currentTime);
      siren.connect(sirenGain);
      sirenGain.connect(ctx.destination);
      sirenGain.gain.setValueAtTime(0.6, ctx.currentTime);
      sirenGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
      
      sirenLFO.start(ctx.currentTime);
      siren.start(ctx.currentTime);
      sirenLFO.stop(ctx.currentTime + duration);
      siren.stop(ctx.currentTime + duration);

      // Deep bass rumble
      const bass = ctx.createOscillator();
      const bassGain = ctx.createGain();
      bass.connect(bassGain);
      bassGain.connect(ctx.destination);
      bass.type = 'sine';
      bass.frequency.setValueAtTime(55, ctx.currentTime);
      bass.frequency.exponentialRampToValueAtTime(35, ctx.currentTime + duration);
      bassGain.gain.setValueAtTime(0.8, ctx.currentTime);
      bassGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
      bass.start(ctx.currentTime);
      bass.stop(ctx.currentTime + duration);
    }
  } catch { /* AudioContext not available */ }
}

/**
 * Simple 2-note notification for admin task completion.
 * Much shorter and less intrusive than the video gen melody.
 */
export function playAdminTaskNotificationSound(type: 'success' | 'error') {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();

    if (type === 'success') {
      // Two ascending dings: G5 → C6
      const notes = [
        { freq: 784, start: 0, duration: 0.18 },
        { freq: 1047, start: 0.2, duration: 0.35 },
      ];
      notes.forEach(note => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(note.freq, ctx.currentTime + note.start);
        gain.gain.setValueAtTime(0.6, ctx.currentTime + note.start);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + note.start + note.duration);
        osc.start(ctx.currentTime + note.start);
        osc.stop(ctx.currentTime + note.start + note.duration);
      });
    } else {
      // Two descending dull tones: E4 → C4
      const notes = [
        { freq: 330, start: 0, duration: 0.2 },
        { freq: 262, start: 0.22, duration: 0.35 },
      ];
      notes.forEach(note => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(note.freq, ctx.currentTime + note.start);
        gain.gain.setValueAtTime(0.5, ctx.currentTime + note.start);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + note.start + note.duration);
        osc.start(ctx.currentTime + note.start);
        osc.stop(ctx.currentTime + note.start + note.duration);
      });
    }
  } catch { /* AudioContext not available */ }
}

export function sendBrowserNotification(title: string, body: string) {
  if (!('Notification' in window)) {
    console.warn('Notification API not supported');
    return;
  }
  
  if (Notification.permission !== 'granted') {
    console.warn('Notification permission not granted:', Notification.permission);
    return;
  }
  
  try {
    const notification = new Notification(title, { 
      body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'kling-video-gen',
      requireInteraction: true,
      silent: false
    });
    
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
    
    console.log('System notification sent:', title);
  } catch (error) {
    console.error('Failed to send notification:', error);
  }
}

export function speakNotification(type: 'success' | 'error') {
  if (!('speechSynthesis' in window)) return;
  
  try {
    const utterance = new SpeechSynthesisUtterance();
    utterance.lang = 'vi-VN';
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    
    if (type === 'success') {
      utterance.text = 'Video đã gen xong rồi bạn ơi!';
    } else {
      utterance.text = 'Ối, Video gen lỗi rồi!';
    }
    
    window.speechSynthesis.speak(utterance);
  } catch { /* TTS not available */ }
}

export function updateTabTitle(type: 'success' | 'error' | 'restore', originalTitle?: string) {
  if (type === 'restore' && originalTitle) {
    document.title = originalTitle;
  } else if (type === 'success') {
    document.title = '✅ Video Gen Complete!';
  } else if (type === 'error') {
    document.title = '❌ Video Gen Failed!';
  }
}
